import { FunctionComponent, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiPlus, mdiPencil, mdiCheck, mdiClose } from "@mdi/js";
import LoadingWidget from "../../../context_providers/modal/loader";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import { urls } from "../../../utils/urls";
import { useAuth } from "../../../context_providers/auth/authProvider";
import { ACTION, MODULE } from "../../../utils/enums";
import {
  ApproveTransformMasterApi,
  GetTransformMastersApi,
  RejectTransformMasterApi,
  TransformApprovalStatus,
  TransformMasterModel,
} from "./transform_master_repo";
import Pagination from "../../../ui/new_components/common/pagination";
import SearchFieldWidget from "../../../ui/new_components/search_field";
import {
  ResponsiveListShell,
  MobileRecordCard,
  MobileRecordCardList,
  EmptyState,
} from "../../../ui/new_components/common/responsive";

const PAGE_SIZE = 20;

// A transform created before this feature shipped has no approval_status at
// all in the DB. The backend grandfathers those in as Approved (see
// approvedOrLegacyMatch in transform_master_repo.ts on the backend) rather
// than silently blocking recipes that were already relied upon — mirror that
// here so the badge/actions agree with what execution actually does.
const effectiveStatus = (
  row: TransformMasterModel,
): TransformApprovalStatus =>
  row.approval_status ?? TransformApprovalStatus.APPROVED;

const statusBadgeClass = (status?: TransformApprovalStatus) => {
  switch (status) {
    case TransformApprovalStatus.APPROVED:
      return "bg-green-50 text-green-700 border border-green-200";
    case TransformApprovalStatus.REJECTED:
      return "bg-red-50 text-red-700 border border-red-200";
    default:
      return "bg-amber-50 text-amber-700 border border-amber-200";
  }
};

const TransformListScreen: FunctionComponent = () => {
  const navigation = useHistory();
  const { showToast } = useToast();
  const { isAuthorised } = useAuth();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<TransformMasterModel[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    TransformApprovalStatus | ""
  >("");
  const [maker, setMaker] = useState(false);
  const [checker, setChecker] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);

  useEffect(() => {
    setMaker(
      isAuthorised({
        module: MODULE.transform_item,
        action: [ACTION.CREATE, ACTION.UPDATE],
      }),
    );
    setChecker(
      isAuthorised({
        module: MODULE.transform_item_approval,
        action: ACTION.UPDATE,
      }),
    );
  }, [isAuthorised]);

  const fetchData = async () => {
    setLoading(true);
    const res = await GetTransformMastersApi({
      page,
      count: PAGE_SIZE,
      search: search.trim() || undefined,
      approval_status: statusFilter || undefined,
    });
    if (res.success) {
      setRows(res.data?.data ?? []);
      setTotal(res.data?.metadata?.total ?? 0);
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  const handleApprove = async (id: string) => {
    setActingId(id);
    const res = await ApproveTransformMasterApi(id);
    if (res.success) {
      showToast({ type: ToastType.success, text: `${id} approved` });
      fetchData();
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setActingId(null);
  };

  const handleReject = async (id: string) => {
    const reason = window.prompt("Reason for rejecting this transform:");
    if (reason === null) return; // cancelled
    if (!reason.trim()) {
      showToast({
        type: ToastType.warning,
        text: "A rejection reason is required",
      });
      return;
    }
    setActingId(id);
    const res = await RejectTransformMasterApi(id, reason.trim());
    if (res.success) {
      showToast({ type: ToastType.success, text: `${id} rejected` });
      fetchData();
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setActingId(null);
  };

  return (
    <>
      <ResponsiveListShell
        title="Transform Data"
        count={total}
        onRefresh={() => fetchData()}
        search={
          <SearchFieldWidget
            placeholder="Search by source item code / name"
            defaulValue={search}
            onClear={() => {
              setSearch("");
              setPage(1);
              fetchData();
            }}
            onSubmit={(val) => {
              setSearch(val);
              setPage(1);
              fetchData();
            }}
          />
        }
        actions={
          <div className="flex items-center gap-2">
            <select
              id="transform_status_filter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as TransformApprovalStatus | "");
                setPage(1);
              }}
              className="border rounded px-2 py-1 text-xs"
            >
              <option value="">All statuses</option>
              {Object.values(TransformApprovalStatus).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            {maker && (
              <button
                id="create_transform_btn"
                onClick={() =>
                  navigation.push(
                    urls.procurement_masters_transform + "/create",
                  )
                }
                className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-white transform text-xs"
              >
                <Icon path={mdiPlus} size={0.8} />
                Create Transform Item
              </button>
            )}
          </div>
        }
      >
        {rows.length > 0 ? (
          <>
            <div className="hidden md:block my-2 border">
              <div className="overflow-auto">
                <table
                  id="transform_table"
                  className="w-full text-xs divide-y divide-gray-200 relative"
                >
                  <thead className="text-xs py-2 bg-white z-10">
                    <tr className="border">
                      <th className="px-3 py-3 text-left">ID</th>
                      <th className="px-3 py-3 text-left">Inputs</th>
                      <th className="px-3 py-3 text-left">Outputs</th>
                      <th className="px-3 py-3 text-left">Created By</th>
                      <th className="px-3 py-3 text-left">Status</th>
                      <th className="px-3 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        id={`transform_row_${row.id}`}
                        key={row.id}
                        className="border text-center"
                      >
                        <td className="px-3 py-3 text-left font-medium whitespace-nowrap">
                          {row.id}
                        </td>
                        <td className="px-3 py-3 text-left">
                          <div className="flex flex-wrap gap-1">
                            {(row.inputs ?? []).map((t, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center rounded-full bg-gray-100 text-gray-700 px-2 py-0.5 text-xs"
                                title={t.item_name}
                              >
                                {t.item_code} × {t.qty}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-left">
                          <div className="flex flex-wrap gap-1">
                            {(row.outputs ?? []).map((t, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 px-2 py-0.5 text-xs"
                                title={t.item_name}
                              >
                                {t.item_code} × {t.qty}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-left whitespace-nowrap">
                          {row.created_by_name || "--"}
                        </td>
                        <td className="px-3 py-3 text-left whitespace-nowrap">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${statusBadgeClass(
                              effectiveStatus(row),
                            )}`}
                            title={row.rejected_reason}
                          >
                            {effectiveStatus(row)}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center justify-center gap-2">
                            {maker && (
                              <button
                                id={`transform_edit_btn_${row.id}`}
                                onClick={() =>
                                  navigation.push(
                                    urls.procurement_masters_transform +
                                      "/update/" +
                                      row.id,
                                  )
                                }
                                className="text-gray-500 hover:text-myPrimaryColor"
                                title="Edit"
                              >
                                <Icon path={mdiPencil} size={0.7} />
                              </button>
                            )}
                            {checker &&
                              effectiveStatus(row) ===
                                TransformApprovalStatus.PENDING && (
                                <>
                                  <button
                                    id={`transform_approve_btn_${row.id}`}
                                    onClick={() =>
                                      handleApprove(row.id as string)
                                    }
                                    disabled={actingId === row.id}
                                    className="text-green-600 hover:text-green-800 disabled:opacity-50"
                                    title="Approve"
                                  >
                                    <Icon path={mdiCheck} size={0.7} />
                                  </button>
                                  <button
                                    id={`transform_reject_btn_${row.id}`}
                                    onClick={() =>
                                      handleReject(row.id as string)
                                    }
                                    disabled={actingId === row.id}
                                    className="text-red-600 hover:text-red-800 disabled:opacity-50"
                                    title="Reject"
                                  >
                                    <Icon path={mdiClose} size={0.7} />
                                  </button>
                                </>
                              )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <MobileRecordCardList>
              {rows.map((row) => (
                <MobileRecordCard
                  id={`transform_row_mobile_${row.id}`}
                  key={row.id}
                  onClick={() =>
                    navigation.push(
                      urls.procurement_masters_transform + "/update/" + row.id,
                    )
                  }
                  title={row.id}
                  fields={[
                    {
                      label: "Inputs",
                      value: (row.inputs ?? [])
                        .map((t) => `${t.item_code} × ${t.qty}`)
                        .join(", "),
                    },
                    {
                      label: "Outputs",
                      value: (row.outputs ?? [])
                        .map((t) => `${t.item_code} × ${t.qty}`)
                        .join(", "),
                    },
                    {
                      label: "Created By",
                      value: row.created_by_name || "-",
                    },
                    {
                      label: "Status",
                      value: effectiveStatus(row),
                    },
                  ]}
                  actions={[
                    ...(maker
                      ? [
                          {
                            icon: mdiPencil,
                            title: "Edit",
                            onClick: () =>
                              navigation.push(
                                urls.procurement_masters_transform +
                                  "/update/" +
                                  row.id,
                              ),
                          },
                        ]
                      : []),
                    ...(checker &&
                    effectiveStatus(row) === TransformApprovalStatus.PENDING
                      ? [
                          {
                            icon: mdiCheck,
                            title: "Approve",
                            onClick: () => handleApprove(row.id as string),
                          },
                          {
                            icon: mdiClose,
                            title: "Reject",
                            onClick: () => handleReject(row.id as string),
                          },
                        ]
                      : []),
                  ]}
                />
              ))}
            </MobileRecordCardList>

            <div
              id="transform_list_pagination"
              className="mt-3 sticky bottom-0 bg-white z-10 border-t flex justify-between items-center px-4 py-2"
            >
              <Pagination
                itemCount={rows.length}
                page={page}
                total={total}
                count={PAGE_SIZE}
                onChange={setPage}
              />
            </div>
          </>
        ) : !loading ? (
          <EmptyState
            title="No transforms defined yet."
            message="Create a transform item to get started."
            imageSrc="/images/nodata.png"
          />
        ) : null}
      </ResponsiveListShell>

      {loading && <LoadingWidget />}
    </>
  );
};

export default TransformListScreen;
